import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, CreditCard, Mail, FileText, Link2, Clock, Landmark } from 'lucide-react';
import { documentsApi, emailApi, billingoApi, statementsApi, syncApi, navUpoApi,
         raiffeisenApi } from '../services/api';
import { Btn, Input, Alert, Modal, Select } from './UI';
import { usePostHog } from '@posthog/react';

const todayYmd = () => new Date().toISOString().slice(0, 10);
const firstOfMonthYmd = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };
const currentMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };

// ISO időbélyeg → "3 órája · 2026-07-14 22:47" jellegű, budapesti helyi idővel.
function fmtLastSync(entry) {
  if (!entry || !entry.at) return { text: 'még nem futott', dim: true };
  const d = new Date(entry.at);
  const now = new Date();
  const diffMin = Math.round((now - d) / 60000);
  let rel;
  if (diffMin < 1) rel = 'épp most';
  else if (diffMin < 60) rel = `${diffMin} perce`;
  else if (diffMin < 60 * 24) rel = `${Math.round(diffMin / 60)} órája`;
  else rel = `${Math.round(diffMin / 1440)} napja`;
  const local = d.toLocaleString('hu-HU', { timeZone: 'Europe/Budapest',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  return { text: `${rel} · ${local}`, info: entry.info, dim: false };
}

// Egy szinkron-forrás kártyája: fejléc + last-sync + saját vezérlők + eredmény.
function SyncSection({ icon: Icon, title, desc, last, children, actions, result, accent = 'var(--primary)' }) {
  const ls = fmtLastSync(last);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
      padding: '0.85rem 0.95rem', marginBottom: '0.75rem', background: 'var(--bg-card)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <Icon size={16} style={{ color: accent, flexShrink: 0 }} />
            <span style={{ fontWeight: 600, fontSize: '0.86rem' }}>{title}</span>
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>{desc}</div>
        </div>
        <div style={{ flexShrink: 0 }}>{actions}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8,
        fontSize: '0.72rem', color: ls.dim ? 'var(--text-dim)' : 'var(--text-muted)' }}>
        <Clock size={12} style={{ flexShrink: 0 }} />
        <span>Utolsó sikeres: <strong style={{ color: ls.dim ? 'var(--text-dim)' : 'var(--text)' }}>{ls.text}</strong></span>
        {ls.info ? <span style={{ color: 'var(--text-dim)' }}>· {ls.info}</span> : null}
      </div>

      {children ? <div style={{ marginTop: 10 }}>{children}</div> : null}
      {result ? <div style={{ marginTop: 10 }}>{result}</div> : null}
    </div>
  );
}

const pill = (active, color) => ({
  padding: '0.28rem 0.65rem', borderRadius: 'var(--radius)', fontSize: '0.74rem',
  border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
  color: active ? color : 'var(--text-muted)', cursor: 'pointer',
});

export default function SyncModal({ open, onClose }) {
  const qc = useQueryClient();
  const posthog = usePostHog();
  const statusQuery = useQuery({
    queryKey: ['sync-status'],
    queryFn: syncApi.status,
    enabled: open,
    refetchOnMount: true,
  });
  const st = statusQuery.data || {};
  const afterSync = () => { qc.invalidateQueries({ queryKey: ['documents'] }); qc.invalidateQueries({ queryKey: ['sync-status'] }); };

  // ── NAV ────────────────────────────────────────────────────────────────
  const [navRange, setNavRange] = useState({ from: firstOfMonthYmd(), to: todayYmd() });
  const [navResult, setNavResult] = useState(null);
  const navMut = useMutation({
    mutationFn: (p) => documentsApi.navSync(p),
    onSuccess: (d) => {
      posthog?.capture('nav_sync_triggered', { synced: d?.synced, total_found: d?.total_found });
      setNavResult(d); afterSync();
    },
    onError: (e) => setNavResult({ _error: e?.response?.data?.detail || e.message || 'Hiba' }),
  });

  // ── NAV adófolyószámla (ÜPO) ───────────────────────────────────────────
  // ⚠ Ez NEM az Online Számla (fentebb): más NAV-rendszer, más regisztráció.
  // Az `idoszakVege` a lekérdezés napja (a NAV arra a napra vonatkozó
  // ÁLLAPOTOT adja) — nem egy dátum-tartomány vége, ezért csak egy dátum kell.
  const [upoDate, setUpoDate] = useState(todayYmd());
  const [upoTipus, setUpoTipus] = useState('KONYVELT_ES_KONYVELESRE_VARO');
  const [upoResult, setUpoResult] = useState(null);
  // A NAV ÜPO cégenkénti kapcsolója (2026-09-27): kikapcsolva a gomb tiltott.
  const { data: upoState = {} } = useQuery({ queryKey: ['nav-upo-state'], queryFn: navUpoApi.state });
  const upoEnabled = upoState.enabled === true;
  const upoMut = useMutation({
    mutationFn: () => navUpoApi.sync({ idoszak_vege: upoDate, tetel_tipus: upoTipus }),
    onSuccess: (d) => {
      posthog?.capture('nav_upo_sync_triggered', {
        items_seen: d?.teteles?.items_seen,
        unchanged: d?.teteles?.unchanged,
      });
      setUpoResult(d); afterSync();
      qc.invalidateQueries({ queryKey: ['nav-upo-state'] });
    },
    onError: (e) => setUpoResult({ _error: e?.response?.data?.detail || e.message || 'Hiba' }),
  });

  // ── E-mail ─────────────────────────────────────────────────────────────
  const [emailMode, setEmailMode] = useState('period');
  const [emailPeriod, setEmailPeriod] = useState(currentMonth());
  const [emailRange, setEmailRange] = useState({ from: todayYmd(), to: todayYmd() });
  const [emailResult, setEmailResult] = useState(null);
  const emailMut = useMutation({
    mutationFn: (p) => emailApi.run(p),
    onSuccess: (d) => {
      posthog?.capture('email_sync_triggered', {
        saved: d?.saved,
        processed: d?.processed,
        statements_saved: d?.statements_saved,
        tax_obligations_saved: d?.tax_obligations_saved,
        mode: emailMode,
      });
      setEmailResult(d); afterSync();
    },
    onError: (e) => setEmailResult({ _error: e?.response?.data?.detail || e.message || 'Hiba' }),
  });

  // ── Revolut ────────────────────────────────────────────────────────────
  const [revResult, setRevResult] = useState(null);
  const revMut = useMutation({
    mutationFn: () => documentsApi.revolutSync(),
    onSuccess: (d) => {
      posthog?.capture('revolut_sync_triggered', { matched: d?.matched, drafts_synced: d?.drafts?.synced });
      setRevResult(d); afterSync();
    },
    onError: (e) => setRevResult({ _error: e?.response?.data?.detail || e.message || 'Hiba' }),
  });

  // ── Rematch ────────────────────────────────────────────────────────────
  const [rematchResult, setRematchResult] = useState(null);
  const rematchMut = useMutation({
    mutationFn: () => statementsApi.rematchAll(),
    onSuccess: (d) => { setRematchResult(d); afterSync(); },
    onError: (e) => setRematchResult({ _error: e?.response?.data?.detail || e.message || 'Hiba' }),
  });

  // ── Raiffeisen kivonatok ───────────────────────────────────────────────
  const [raifStmtResult, setRaifStmtResult] = useState(null);
  const raifStmtMut = useMutation({
    mutationFn: () => raiffeisenApi.syncStatements(),
    onSuccess: (d) => {
      posthog?.capture('raiffeisen_statement_sync_triggered', {
        ingested: d?.ingested, skipped: d?.skipped, accounts: d?.accounts,
      });
      setRaifStmtResult(d);
      afterSync();
      // A kivonat-listát is frissítjük – új kivonat keletkezhetett.
      qc.invalidateQueries({ queryKey: ['statements'] });
    },
    onError: (e) => setRaifStmtResult({ _error: e?.response?.data?.detail || e.message || 'Hiba' }),
  });

  // ── Billingo ───────────────────────────────────────────────────────────
  const [billingoMonth, setBillingoMonth] = useState(currentMonth());
  const [billingoResult, setBillingoResult] = useState(null);
  const billingoMut = useMutation({
    mutationFn: (month) => {
      const [y, m] = month.split('-');
      const last = String(new Date(parseInt(y), parseInt(m), 0).getDate()).padStart(2, '0');
      return billingoApi.sync(`${y}-${m}-01`, `${y}-${m}-${last}`);
    },
    onSuccess: (d) => {
      posthog?.capture('billingo_sync_triggered', { synced: d?.synced, total_found: d?.total_found, month: billingoMonth });
      setBillingoResult(d); afterSync();
    },
    onError: (e) => setBillingoResult({ _error: e?.response?.data?.detail || e.message || 'Hiba' }),
  });

  const errAlert = (r) => r?._error ? <Alert type="danger">{r._error}</Alert> : null;

  return (
    <Modal open={open} onClose={onClose} title="Szinkronizálás" width={640}>
      <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '0.9rem' }}>
        Minden bizonylat- és tranzakció-forrás egy helyen. Forrásonként látod, mikor futott
        utoljára sikeresen.
      </p>

      {/* NAV */}
      <SyncSection icon={FileText} title="NAV Online Számla" last={st.nav}
        desc="Bejövő számlák letöltése a NAV-tól a megadott időszakra."
        actions={
          <Btn size="sm" icon={RefreshCw} loading={navMut.isPending}
            disabled={!navRange.from || !navRange.to}
            onClick={() => { setNavResult(null); navMut.mutate({ date_from: navRange.from, date_to: navRange.to }); }}>
            Szinkron
          </Btn>}
        result={navResult && (errAlert(navResult) || (
          <Alert type="success"><strong>{navResult.synced} új számla</strong> · {navResult.total_found} talált a NAV-nál.</Alert>))}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Input label="Tól" type="date" lang="hu" value={navRange.from} onChange={e => setNavRange(r => ({ ...r, from: e.target.value }))} />
          <Input label="Ig" type="date" lang="hu" value={navRange.to} onChange={e => setNavRange(r => ({ ...r, to: e.target.value }))} />
        </div>
      </SyncSection>

      {/* NAV adófolyószámla — MÁS rendszer, mint az Online Számla fentebb */}
      <SyncSection icon={Landmark} title="NAV adófolyószámla" last={st.nav_upo} accent="#0ea5e9"
        desc="Tartozás/túlfizetés adónemenként, a NAV Ügyfélportál gépi felületéről. Beállítás: Beállítások → NAV adófolyószámla."
        actions={
          <Btn size="sm" icon={RefreshCw} loading={upoMut.isPending} disabled={!upoDate || !upoEnabled}
            title={upoEnabled ? undefined : 'A NAV ÜPO ki van kapcsolva (Beállítások → NAV adófolyószámla).'}
            onClick={() => { setUpoResult(null); upoMut.mutate(); }}>
            Szinkron
          </Btn>}
        result={upoResult && (errAlert(upoResult) || (
          <Alert type={upoResult.teteles?.unchanged ? 'info' : 'success'}>
            {upoResult.teteles?.unchanged
              ? <>A NAV válasza <strong>változatlan</strong> az előző lekérdezés óta.</>
              : <><strong>{upoResult.teteles?.items_seen ?? 0} tétel</strong> ·{' '}
                  {upoResult.teteles?.adonem_count ?? 0} adónem
                  {upoResult.teteles?.items_inserted
                    ? <> · {upoResult.teteles.items_inserted} új</> : null}</>}
            {upoResult.teteles?.vanished_count > 0 &&
              <> · <strong>{upoResult.teteles.vanished_count}</strong> tétel kikerült a NAV-ból</>}
            {upoResult.teteles?.warnings?.length > 0 && (
              <ul style={{marginTop:6,paddingLeft:16,marginBottom:0,fontSize:'0.76rem'}}>
                {upoResult.teteles.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              </ul>)}
          </Alert>))}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
          <Input label="Állapot napja" type="date" lang="hu" value={upoDate}
            onChange={e => setUpoDate(e.target.value)} />
          <Select label="Tételek" value={upoTipus} onChange={e => setUpoTipus(e.target.value)}
            options={[
              { value: 'KONYVELT_ES_KONYVELESRE_VARO', label: 'Könyvelt + könyvelésre váró' },
              { value: 'KONYVELT', label: 'Csak könyvelt' },
            ]}/>
        </div>
      </SyncSection>

      {/* E-mail */}
      <SyncSection icon={Mail} title="E-mail (invoice@)" last={st.email}
        desc="A postafiók feldolgozása: számlák, kivonatok, bér/adó bizonylatok."
        actions={
          <Btn size="sm" icon={RefreshCw} loading={emailMut.isPending}
            disabled={emailMode === 'period' ? !emailPeriod : !(emailRange.from && emailRange.to)}
            onClick={() => { setEmailResult(null); emailMut.mutate(
              emailMode === 'period' ? { period: emailPeriod } : { date_from: emailRange.from, date_to: emailRange.to }); }}>
            Szinkron
          </Btn>}
        result={emailResult && (errAlert(emailResult) || (
          <Alert type="success">
            <strong>{emailResult.saved} új bizonylat</strong> · feldolgozva {emailResult.processed} ·
            adó {emailResult.tax_obligations_saved || 0} · bér {emailResult.payroll_items_saved || 0} ·
            kivonat {emailResult.statements_saved || 0} ({emailResult.statement_transactions_saved || 0} tr.) ·
            NAV-egyezés {emailResult.nav_matched || 0}
            {emailResult.errors?.length > 0 && (
              <ul style={{ marginTop: 6, paddingLeft: 16, marginBottom: 0, fontSize: '0.76rem' }}>
                {emailResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              </ul>)}
          </Alert>))}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button style={pill(emailMode === 'period', 'var(--primary)')} onClick={() => setEmailMode('period')}>Egy hónap</button>
          <button style={pill(emailMode === 'range', 'var(--primary)')} onClick={() => setEmailMode('range')}>Dátum tartomány</button>
        </div>
        {emailMode === 'period' ? (
          <Input label="Célhónap" type="month" lang="hu" value={emailPeriod} onChange={e => setEmailPeriod(e.target.value)} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Input label="Tól" type="date" lang="hu" value={emailRange.from} onChange={e => setEmailRange(r => ({ ...r, from: e.target.value }))} />
            <Input label="Ig" type="date" lang="hu" value={emailRange.to} onChange={e => setEmailRange(r => ({ ...r, to: e.target.value }))} />
          </div>
        )}
      </SyncSection>

      {/* Revolut */}
      <SyncSection icon={CreditCard} title="Revolut banki egyeztetés" last={st.revolut} accent="#10b981"
        desc="Az elmúlt 90 nap tranzakcióit egyezteti a bizonylatokkal (fizetve jelölés + draft-státusz)."
        actions={
          <Btn size="sm" icon={RefreshCw} loading={revMut.isPending}
            onClick={() => { setRevResult(null); revMut.mutate(); }}>
            Szinkron
          </Btn>}
        result={revResult && (errAlert(revResult) || (
          <Alert type={revResult.matched ? 'success' : 'info'}>
            {revResult.matched ? `${revResult.matched} bizonylat fizetve jelölve.` : 'Nem találtam új egyezést.'}
            {revResult.drafts?.synced > 0 && ` · draft: ${revResult.drafts.synced} ellenőrizve${revResult.drafts.completed ? `, ${revResult.drafts.completed} jóváhagyva` : ''}`}
          </Alert>))} />

      {/* Raiffeisen kivonat */}
      <SyncSection icon={Landmark} title="Raiffeisen kivonatok" last={st.raiffeisen} accent="#fbbf24"
        desc="A jóváhagyott számlák új kivonatait tölti le a bankból és feldolgozza (a Kivonatok
              menüben jelennek meg). A bank csak a SAJÁT, kész kivonat-dokumentumait adja, és max
              1 évre visszamenőleg — tetszőleges időszakra nincs kivonat-generálás."
        actions={
          <Btn size="sm" icon={RefreshCw} loading={raifStmtMut.isPending}
            onClick={() => { setRaifStmtResult(null); raifStmtMut.mutate(); }}>
            Szinkron
          </Btn>}
        result={raifStmtResult && (errAlert(raifStmtResult) || (
          <Alert type={raifStmtResult.ingested ? 'success'
                       : (raifStmtResult.failed ? 'warning' : 'info')}>
            {raifStmtResult.note ? raifStmtResult.note : (<>
              <strong>{raifStmtResult.ingested} új kivonat</strong>
              {' · '}{raifStmtResult.found} talált a banknál
              {raifStmtResult.skipped > 0 && ` · ${raifStmtResult.skipped} már megvolt`}
              {raifStmtResult.failed > 0 && ` · ${raifStmtResult.failed} hibás`}
              {' · '}{raifStmtResult.accounts} számla
              {raifStmtResult.found === 0 && raifStmtResult.accounts > 0 &&
                ' — a banknál nincs kivonat erre az időszakra.'}
            </>)}
          </Alert>))} />

      {/* Rematch */}
      <SyncSection icon={Link2} title="Kivonat-újrapárosítás" last={st.rematch} accent="#f59e0b"
        desc="Minden jóváhagyott kivonat tranzakcióit újra illeszti a bizonylatokhoz."
        actions={
          <Btn size="sm" icon={RefreshCw} loading={rematchMut.isPending}
            onClick={() => { setRematchResult(null); rematchMut.mutate(); }}>
            Futtatás
          </Btn>}
        result={rematchResult && (errAlert(rematchResult) || (
          <Alert type="success">{rematchResult.matched} új tranzakció–bizonylat társítás.</Alert>))} />

      {/* Billingo */}
      <SyncSection icon={FileText} title="Billingo (kimenő)" last={st.billingo} accent="#a855f7"
        desc="Kimenő számlák szinkronizálása a Billingóból a megadott hónapra."
        actions={
          <Btn size="sm" icon={RefreshCw} loading={billingoMut.isPending} disabled={!billingoMonth}
            onClick={() => { setBillingoResult(null); billingoMut.mutate(billingoMonth); }}>
            Szinkron
          </Btn>}
        result={billingoResult && (errAlert(billingoResult) || (
          <Alert type="success">
            <strong>{billingoResult.synced} új</strong> · {billingoResult.total_found} talált.
            {/* M7: több fióknál fiókonkénti bontás — egy holt fiók ne tűnjön el az összesítésben. */}
            {(billingoResult.accounts || []).length > 1 && (
              <span style={{ display: 'block', fontSize: '0.75rem', marginTop: 4 }}>
                {billingoResult.accounts.map(a =>
                  `${a.label || `fiók #${a.asset_id}`}: ${a.found} talált`).join(' · ')}
              </span>
            )}
          </Alert>))}>
        <Input label="Hónap" type="month" lang="hu" value={billingoMonth} onChange={e => setBillingoMonth(e.target.value)} />
      </SyncSection>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <Btn variant="ghost" onClick={onClose}>Bezárás</Btn>
      </div>
    </Modal>
  );
}
